import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, LogOut, Shield, Users } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import PasswordChangeDialog from '@/components/PasswordChangeDialog';

const Admin = () => {
  const { user, login, logout, loading, token } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [loginToken, setLoginToken] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      // Check if first login
      if (result.first_login) {
        setLoginToken(localStorage.getItem('token'));
        setShowPasswordChange(true);
      }
      // Don't reload - let the AuthContext handle the state update
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  const handlePasswordChanged = () => {
    setShowPasswordChange(false);
    setLoginToken(null);
    window.location.reload();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-surface py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-primary-700 rounded-xl flex items-center justify-center">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-heading text-center">Admin Login</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access the admin panel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                />
              </div>
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-primary-700 hover:bg-primary-900"
                disabled={isLoading}
                data-testid="admin-login-button"
              >
                {isLoading ? (
                  <>
                    <Lock className="mr-2 h-4 w-4 animate-pulse" />
                    Logging in...
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Login
                  </>
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/admin/forgot-password" className="text-sm text-primary-700 hover:text-primary-900">
                Forgot your password?
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
      <PasswordChangeDialog 
        isOpen={showPasswordChange}
        onPasswordChanged={handlePasswordChanged}
        token={loginToken || token}
      />
    </>
    );
  }

  // Show password change dialog even when logged in (for first login)
  if (showPasswordChange) {
    return (
      <PasswordChangeDialog 
        isOpen={showPasswordChange}
        onPasswordChanged={handlePasswordChanged}
        token={loginToken || token}
      />
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-semibold text-slate-900 mb-2">
              Admin Dashboard
            </h1>
            <p className="text-slate-600">Welcome back, {user.username}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="admin-logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {user.role === 'super_admin' && (
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-purple-700" />
                  <span>Admin Users</span>
                </CardTitle>
                <CardDescription>Manage admin accounts and permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Add, remove admin users and view audit trail.
                </p>
                <Link to="/admin/users">
                  <Button className="w-full bg-purple-700 hover:bg-purple-900 text-white">
                    Manage Admins
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Representatives</CardTitle>
              <CardDescription>Manage representative information</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Add, edit, or remove representatives from the directory.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-primary-700 hover:bg-primary-900">
                  Manage Representatives
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Articles & Blogs</CardTitle>
              <CardDescription>Manage expert insights</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Add, edit articles and manage the blog content.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-primary-700 hover:bg-primary-900">
                  Manage Articles
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Podcasts</CardTitle>
              <CardDescription>Manage \"Your House\" episodes</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Add or update podcast episodes and YouTube links.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-primary-700 hover:bg-primary-900">
                  Manage Podcasts
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Educational Content</CardTitle>
              <CardDescription>Update legislative information</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Edit educational content about the legislature.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-primary-700 hover:bg-primary-900">
                  Manage Content
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Engagement Tools</CardTitle>
              <CardDescription>Modify engagement guides</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Update citizen engagement tool instructions.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-primary-700 hover:bg-primary-900">
                  Manage Tools
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Templates</CardTitle>
              <CardDescription>Manage document templates</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Add or update petition and memo templates.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-primary-700 hover:bg-primary-900">
                  Manage Templates
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>FAQs</CardTitle>
              <CardDescription>Update frequently asked questions</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Add, edit, or remove FAQ entries.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-primary-700 hover:bg-primary-900">
                  Manage FAQs
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Content</CardTitle>
              <CardDescription>Unified content manager</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Access the full content management interface.
              </p>
              <Link to="/admin/manage">
                <Button className="w-full bg-accent hover:bg-accent-hover text-white">
                  Open CMS
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 bg-blue-50 rounded-2xl p-8 border border-blue-200">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
            Admin Panel Information
          </h3>
          <p className="text-slate-700 mb-4">
            This admin panel provides content management capabilities for the Nigeria Citizen Engagement Toolkit. 
            You can manage all aspects of the platform including representatives, educational content, templates, and FAQs.
          </p>
          <p className="text-sm text-slate-600">
            Note: This is a simplified admin interface. Full CRUD operations can be implemented using the backend API endpoints.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Admin;
